/**
 * A box over the page for one short task: choose apps, register an app, confirm
 * a removal.
 *
 * The browser's own <dialog>: it keeps focus inside, closes on Escape and hides
 * the page behind it. Mounted means open. The caller renders it only while the
 * task is going on, so a form starts fresh each time.
 */

import { useEffect, useId, useRef, type ReactNode } from "react";
import { X } from "lucide-react";

import { Button } from "@/components/kit";

import styles from "./workflows.module.css";

export function Dialog({
  title,
  note,
  onClose,
  footer,
  wide = false,
  children,
}: {
  title: string;
  note?: ReactNode;
  onClose: () => void;
  footer?: ReactNode;
  wide?: boolean;
  children: ReactNode;
}) {
  const box = useRef<HTMLDialogElement>(null);
  const titleId = useId();

  useEffect(() => {
    const dialog = box.current;
    if (dialog && !dialog.open) dialog.showModal();
    return () => dialog?.close();
  }, []);

  return (
    <dialog
      ref={box}
      className={wide ? `${styles.dialog} ${styles.dialogWide}` : styles.dialog}
      aria-labelledby={titleId}
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      // A press on the dark area around the box, which is the dialog itself.
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className={styles.dialogInner}>
        <div className={styles.dialogHead}>
          <div>
            <h2 id={titleId} className={styles.dialogTitle}>
              {title}
            </h2>
            {note && <p className={styles.dialogNote}>{note}</p>}
          </div>
          <Button tone="subtle" icon aria-label="Close" onClick={onClose}>
            <X size={16} />
          </Button>
        </div>
        <div className={styles.dialogBody}>{children}</div>
        {footer && <div className={styles.dialogFoot}>{footer}</div>}
      </div>
    </dialog>
  );
}
